import { useMemo } from "react";
import { AutoComplete, Spin, Tooltip } from "antd";
import { CheckCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";

const MATCH_EXACT = "exactly matches";
const MATCH_CONTAINS = "contains";
const MATCH_REGEX = "matches regex";

// A `contains` fragment can match hundreds of tags; the tooltip is for
// recognising what you hit, not for reading the whole list.
const TOOLTIP_LIMIT = 25;

// The rule builder writes "tag ID"; the exclusion lists write "tag id".
const isTagIdType = (tagType) =>
  String(tagType || "").toLowerCase().includes("id");

/**
 * Free-text tag input with suggestions drawn from the tags this company has
 * actually fired.
 *
 * Deliberately an AutoComplete and not a Select: with match types `contains`
 * and `matches regex` the saved value is a fragment, not a whole tag name, so
 * the field must never coerce what the user typed. Suggestions are a reference,
 * and the hint below reports what the value would really match.
 */
export const TagValueAutoComplete = ({
  value,
  onChange,
  tagType,
  matchType,
  tags,
  loading = false,
  error = false,
  placeholder,
  style,
  minChars = 3,
  minCharsForId = 1,
  maxOptions = 50,
}) => {
  const useTagId = isTagIdType(tagType);

  // Tag IDs can be a single digit, so gating them behind three characters would
  // hide the very tags the field is meant to find.
  const activeMinChars = useTagId ? minCharsForId : minChars;

  // One entry per distinct suggestable value, carrying the other identifier as
  // secondary text (a tag ID means nothing to a human without its name).
  const candidates = useMemo(() => {
    const byValue = new Map();
    (tags || []).forEach((t) => {
      const primary = useTagId ? t.tag_id : t.tag_name;
      const secondary = useTagId ? t.tag_name : t.tag_id;
      if (!primary || byValue.has(primary)) return;
      byValue.set(primary, { value: primary, secondary });
    });
    return [...byValue.values()];
  }, [tags, useTagId]);

  // The identifier the field is *not* asking for, so a value that only exists
  // over there can be called out as the wrong kind rather than a dead rule.
  const otherIdentifiers = useMemo(() => {
    const set = new Set();
    (tags || []).forEach((t) => {
      const other = useTagId ? t.tag_name : t.tag_id;
      if (other) set.add(other);
    });
    return set;
  }, [tags, useTagId]);

  const typed = value || "";
  const query = typed.trim();

  const options = useMemo(() => {
    if (query.length < activeMinChars || candidates.length === 0) return [];

    const lower = query.toLowerCase();
    const matches = candidates
      .filter((c) => c.value.toLowerCase().includes(lower))
      .sort((a, b) => {
        // Prefix matches are what the user is most likely reaching for.
        const aPrefix = a.value.toLowerCase().startsWith(lower);
        const bPrefix = b.value.toLowerCase().startsWith(lower);
        if (aPrefix !== bPrefix) return aPrefix ? -1 : 1;
        return a.value.toLowerCase().localeCompare(b.value.toLowerCase());
      });

    const shown = matches.slice(0, maxOptions).map((c) => ({
      value: c.value,
      label: (
        <span className="tag-option">
          <span className="tag-option-value">{c.value}</span>
          {c.secondary ? (
            <span className="tag-option-secondary">{c.secondary}</span>
          ) : null}
        </span>
      ),
    }));

    if (matches.length > maxOptions) {
      shown.push({
        value: `__more__${matches.length}`,
        disabled: true,
        label: (
          <span className="tag-option-secondary">
            +{matches.length - maxOptions} more — keep typing to narrow
          </span>
        ),
      });
    }

    return shown;
  }, [candidates, query, activeMinChars, maxOptions]);

  // What the value would really match, using the rule's own semantics. Advisory
  // only — the alerting engine is the authority, so never block on this.
  const hint = useMemo(() => {
    if (loading || error || candidates.length === 0) return null;

    // Below the threshold there is nothing to report yet — say how to get
    // suggestions instead of leaving the field looking inert.
    if (query.length < activeMinChars) return { kind: "min-chars" };

    // One predicate, reused against both the selected identifier and the other
    // one, so "does this match?" means the same thing in both checks.
    let test;
    if (matchType === MATCH_REGEX) {
      let re;
      try {
        re = new RegExp(typed);
      } catch (e) {
        return { kind: "invalid-regex" };
      }
      test = (v) => re.test(v);
    } else if (matchType === MATCH_CONTAINS) {
      test = (v) => v.includes(typed);
    } else {
      test = (v) => v === typed;
    }

    const matched = candidates.filter((c) => test(c.value));
    if (matched.length > 0) return { kind: "ok", matched };

    // Casing is an invisible way for an exact-match rule to never fire.
    if (matchType === MATCH_EXACT || !matchType) {
      const ci = candidates.find(
        (c) => c.value.toLowerCase() === typed.toLowerCase(),
      );
      if (ci) return { kind: "case", suggestion: ci.value };
    }

    // Nothing here, but it exists as the other identifier — the selector is
    // wrong, not the value. Note this can't misfire on unnamed tags, whose
    // name falls back to their ID and so matches above.
    if ([...otherIdentifiers].some(test)) return { kind: "wrong-type" };

    return { kind: "none" };
  }, [
    candidates,
    otherIdentifiers,
    typed,
    query,
    activeMinChars,
    matchType,
    loading,
    error,
  ]);

  const renderHint = () => {
    if (!hint) return null;

    const plural = useTagId ? "tag IDs" : "tags";
    const singular = useTagId ? "tag ID" : "tag name";
    const otherSingular = useTagId ? "tag name" : "tag ID";

    if (hint.kind === "min-chars") {
      return (
        <span className="tag-match-hint is-muted">
          <span className="tag-match-text">
            {activeMinChars > 1
              ? `Type at least ${activeMinChars} characters to see matching ${plural}`
              : `Type to see matching ${plural}`}
          </span>
        </span>
      );
    }

    if (hint.kind === "ok") {
      const names = hint.matched.map((c) => c.value);
      const shown = names.slice(0, TOOLTIP_LIMIT);
      const rest = names.length - shown.length;
      return (
        <Tooltip
          placement="bottomLeft"
          title={
            <div className="tag-match-tooltip">
              {shown.map((n) => (
                <div key={n}>{n}</div>
              ))}
              {rest > 0 ? <div className="is-muted">…and {rest} more</div> : null}
            </div>
          }
        >
          <span className="tag-match-hint is-ok tag-match-hoverable">
            <CheckCircleOutlined />
            <span className="tag-match-text">
              Matches {names.length} {names.length === 1 ? singular : plural}
            </span>
          </span>
        </Tooltip>
      );
    }

    const text = {
      "invalid-regex": "Invalid regular expression",
      case: `No exact match — did you mean "${hint.suggestion}"?`,
      "wrong-type": `That's a ${otherSingular}, not a ${singular} — switch this field to ${otherSingular}`,
      none: `Doesn't match any ${singular}`,
    }[hint.kind];

    return (
      <span className="tag-match-hint is-warn">
        <ExclamationCircleOutlined />
        <span className="tag-match-text">{text}</span>
      </span>
    );
  };

  return (
    <div className="tag-value-autocomplete" style={style}>
      <AutoComplete
        value={typed}
        onChange={(val) => onChange(val ?? "")}
        options={options}
        filterOption={false}
        // The list is already capped at maxOptions, so virtual scrolling buys
        // nothing and only hides options from assistive tech.
        virtual={false}
        popupClassName="custom-dropdown tag-value-dropdown"
        placeholder={placeholder}
        style={{ width: "100%" }}
        // null keeps antd from popping an empty dropdown below the 3-character
        // threshold, or when nothing matches.
        notFoundContent={
          loading && query.length >= minChars ? <Spin size="small" /> : null
        }
      />
      {/* Always rendered, even when empty: the slot reserves its line so the
          input never shifts as the hint appears, changes, or clears. */}
      <div className="tag-match-hint-slot">{renderHint()}</div>
    </div>
  );
};

export default TagValueAutoComplete;
